/**
 * Wire types.
 *
 * Mirrors apps/api/src/realtime/realtime.events.ts by hand. A shared package
 * would be tidier, but the SPA is built by a separate Dockerfile stage and
 * keeping it dependency-free of the API keeps that build fast. The tradeoff is
 * that these must be updated together -- a mismatch is a runtime bug, not a
 * compile error.
 */

import type { SlaSnapshot } from "@helpdesk/sla";

export type { SlaSnapshot };

/** Two states. Closed is not terminal -- a customer reply reopens it. */
export type TicketStatus = "open" | "closed";

export type MessageKind = "inbound" | "outbound" | "note";
export type Delivery = "none" | "queued" | "sent" | "failed";

/** Every route a customer can reach the desk on. See the note on the table. */
export type ChannelType = "gmail" | "whatsapp" | "sms" | "webform" | "ebay";

/**
 * A channel that has stopped, or has replies that did not go out, as the
 * banner across every page shows it. The API decides who sees which -- see
 * `channels/channel-alerts.ts`.
 */
export interface ChannelAlert {
  channelId: number;
  type: ChannelType;
  address: string;
  displayName: string;
  /** Present while it is stalled; `since` is its last success, if it had one. */
  stalled: { since: string | null; error: string } | null;
  /** Newest first, and only the first few -- `failedCount` is all of them. */
  failedReplies: Array<{
    messageId: number;
    ticketId: number;
    subject: string | null;
    sentAt: string;
  }>;
  failedCount: number;
}

/** One reply that did not go out, as the failed-replies page lists it. */
export interface FailedReply {
  messageId: number;
  ticketId: number;
  channelId: number;
  channelAddress: string;
  subject: string | null;
  /** When it was written and queued, not when it gave up. */
  sentAt: string;
  authorName: string | null;
  deliveryError: string | null;
}

export interface Channel {
  id: number;
  type: ChannelType;
  /**
   * An email address for Gmail, an E.164 phone number for WhatsApp and SMS,
   * and the page itself for a web form -- which has neither.
   */
  address: string;
  slug: string;
  displayName: string;
  color: string;
  signatureHtml: string | null;
  config: Record<string, unknown> | null;
  isActive: boolean;
  /** Pulse's own notification emails go out from this address. At most one. */
  sendsNotifications: boolean;
  lastSyncedAt: string | null;
  lastSyncError: string | null;
  watchExpiresAt: number | null;
  /**
   * Why push is not happening: Gmail's refusal, or a watch that is missing or
   * has expired. Null when push is fine or not configured.
   */
  watchError: string | null;
  backfilledAt: string | null;
  /** Null on channels that have no such concept, rather than a misleading false. */
  pushActive: boolean | null;
}

export interface ChannelWrite {
  type: ChannelType;
  address: string;
  slug: string;
  displayName: string;
  color: string;
  isActive: boolean;
  /**
   * Send Pulse's notification emails from here. Optional, and omitted when a
   * channel is created: a new channel becoming the sender the moment it is
   * added is not a thing anybody asked for.
   */
  sendsNotifications?: boolean;
  /**
   * Per-type adapter settings. WhatsApp needs `phoneNumberId` and the server
   * refuses the channel without one; the other types have none today.
   */
  config?: Record<string, unknown> | null;
}

export interface User {
  id: number;
  email: string;
  name: string;
  avatarUrl: string | null;
  /** Their chosen avatar tint, or null to derive one from the id. */
  avatarColor: string | null;
  /**
   * A glyph they picked for themselves, shown before their name in the
   * sidebar. Theirs rather than the viewer's, so it means the same person on
   * everybody's screen -- which is what makes it worth learning.
   */
  emoji: string | null;
  role: "agent" | "admin";
}

/** The newest internal note on a ticket, as the queue lists show it. */
export interface TicketNote {
  /** Plain text, already trimmed to ~500 characters by the API. */
  snippet: string | null;
  authorName: string | null;
  at: string;
}

export interface TicketSummary {
  id: number;
  channelId: number;
  /**
   * How the customer reached us, and which channel it was.
   *
   * Sent with the ticket rather than looked up from `channelId` against the
   * channel list: the queue would otherwise need a second request before it
   * could draw its first row, to render one icon.
   */
  channelType: ChannelType;
  channelName: string;
  subject: string;
  status: TicketStatus;
  assigneeId: number | null;
  contactEmail: string | null;
  contactName: string | null;
  /**
   * The CRM rows behind those names, so a company or a person on a ticket can
   * be opened without looking them up by address first. Null on a ticket ingest
   * could not attribute; `companyId` alone is null for an unfiled contact.
   */
  contactId: number | null;
  companyId: number | null;
  companyName: string | null;
  /**
   * The person's own line and the company switchboard. Both on the summary so
   * the details panel can offer a number to ring without a CRM lookup; either
   * may be null, and a ticket routinely has only the switchboard.
   */
  contactPhone: string | null;
  companyPhone: string | null;
  /**
   * The newest internal note, or null if the ticket has none. Drawn as one
   * marked line in the preview list -- see `PreviewList`.
   */
  lastNote: TicketNote | null;
  assigneeName: string | null;
  lastMessageAt: string;
  lastMessagePreview: string | null;
  isSpam: boolean;
  pipelineId: number | null;
  awaitingResponseSince: string | null;
  orderNumber: string | null;
  quoteNumber: string | null;
  returnsReference: string | null;
  laneTask: string | null;
  /**
   * The server's SLA reading at response time. The UI recomputes this locally
   * every second (see lib/sla.ts) so the countdown stays live without polling;
   * this is what the server sorted on.
   */
  sla: SlaSnapshot;
}

/** Sidebar badge numbers. Every value counts open work except `closed`. */
export interface ViewCounts {
  open: number;
  closed: number;
  unassigned: number;
  mine: number;
  myTeam: number;
  /**
   * Tickets the signed-in agent has been @mentioned in.
   *
   * Matches what the mentions view lists, so the badge and the screen it opens
   * always agree. `mentionsUnread` is the subset not yet looked at, and only
   * decides whether the badge is highlighted.
   */
  mentions: number;
  mentionsUnread: number;
  spam: number;
  /**
   * Open tickets past their response target.
   *
   * A subset of `open`, not a separate pile -- the two are meant to be read
   * together, which is why the view sits directly under it.
   */
  overdue: number;
  /** Keyed by user id. Absent means zero. */
  byAssignee: Record<number, number>;
}

/** A trimmed ticket, for the "other conversations" list. */
export interface RelatedTicket {
  id: number;
  subject: string;
  status: TicketStatus;
  lastMessageAt: string;
}

/** A Gmail-filter-style triage rule. Conditions AND together. */
/** An agent plus their signature, for the settings screen. */
/** An alias an agent may send as. */
export interface SendFromOption {
  id: number;
  address: string;
  displayName: string;
  isDefault: boolean;
}

/** An agent with their granted aliases, for settings. */
export interface SendFromGrant {
  id: number;
  name: string;
  email: string;
  channelIds: number[];
  defaultChannelId: number | null;
}

/** An agent row for settings, including unclaimed invitations. */
export interface SavedView {
  id: number;
  name: string;
  ownerUserId: number;
  isShared: boolean;
  domains: string[] | null;
  pipelineIds: number[] | null;
  slaState: "breached" | "at_risk" | "ok" | null;
  status: "open" | "closed" | "all";
  position: number;
}

export interface MySettings {
  id: number;
  name: string;
  email: string;
  avatarColor: string | null;
  /** The glyph shown before this agent's name in everybody's sidebar. */
  emoji: string | null;
  /**
   * Which Lane board their CRM tasks file to, as a team slug.
   *
   * Null means "follow my team", which is the default and what the desk did
   * before this was a choice. Private to its owner, so it is here and not on
   * `User` -- unlike the emoji, which colleagues see.
   */
  crmBoard: string | null;
  /** What the company list opens on. Everybody's unless they chose otherwise. */
  crmCompaniesScope: "all" | "mine";
  /**
   * The Helpdesk view this agent lands on, as a `/v/:key` segment. Null is
   * All open tickets, the default -- see `landingView` for what happens when
   * it names something that has gone.
   */
  helpdeskDefaultView: string | null;
}

/** Which product's section of the notification settings a switch sits under. */
export type NotificationProduct = "helpdesk" | "crm" | "sales" | "meetings";

/**
 * One switch, as the server describes it.
 *
 * The label and description come from the server rather than being written
 * here, so the catalogue is stated once. Adding a notification is a change to
 * `packages/db/src/notifications.ts` and nothing else.
 */
export interface NotificationPreference {
  key: string;
  product: NotificationProduct;
  label: string;
  description: string;
  /** The bell and its count. */
  enabled: boolean;
  /** Also emailed. Off everywhere until somebody asks for it. */
  email: boolean;
  /** True when this is still the default rather than a choice somebody made. */
  isDefault: boolean;
}

/**
 * A browser's push subscription, exactly as `PushSubscription.toJSON()` emits it.
 *
 * Declared here rather than beside the push helpers so `api.ts` can name it
 * without importing from a module that imports `api.ts` back.
 */
export interface PushSubscriptionPayload {
  /** The URL the push service will accept messages for this browser at. */
  endpoint: string;
  keys: {
    /** The browser's public key. Each payload is encrypted to it. */
    p256dh: string;
    /** Shared secret mixed into the key derivation. */
    auth: string;
  };
}

/** One row in the bell. */
export interface AppNotification {
  id: number;
  eventKey: string;
  title: string;
  body: string;
  href: string;
  read: boolean;
  at: string;
}

export interface TicketWatcher {
  userId: number;
  name: string;
  avatarUrl: string | null;
}

export type ApiKeyScope = "read_only" | "read_write";

/**
 * A key as it can be shown after it has been created.
 *
 * There is no `token` here, and deliberately so -- only its hash is stored, so
 * the secret exists exactly once, in the response to the request that minted it.
 * `CreatedApiKey` is the one shape that carries it.
 */
export interface ApiKey {
  id: number;
  name: string;
  scope: ApiKeyScope;
  /** Credit this key's work to the key rather than to the agent who owns it. */
  attributeToKey: boolean;
  /** `hdsk_********_a1b2` -- enough to tell two keys apart, and no more. */
  maskedToken: string;
  createdAt: string;
  lastUsedAt: string | null;
  expiresAt: string | null;
}

export interface CreatedApiKey extends ApiKey {
  /** Shown once. Not recoverable afterwards, by us or by anyone. */
  token: string;
}

export interface BrandImage {
  id: number;
  name: string;
  mimeType: string;
  sizeBytes: number;
  createdAt: string;
}

export interface AgentAccount {
  id: number;
  name: string;
  email: string;
  role: "agent" | "admin";
  isActive: boolean;
  /** Their desk extension, for calls pushed over `/v1`. Not shown in Settings. */
  extension: string | null;
  /**
   * The phone system's SIP user they are linked to, which is how the synced
   * call history is attributed to them. Null until an admin links them.
   */
  yayUserUuid: string | null;
  invitedAt: string | null;
  lastSeenAt: string | null;
  /** True while they have been invited but never signed in. */
  pending: boolean;
  /**
   * Whether identify will actually let them in.
   *
   * "unknown" means the lookup failed and "unconfigured" that there is no
   * identify to ask -- the normal state on a machine running the dev-login
   * bypass. Both are statements about the server, not about the person, and
   * must never be rendered as a problem with their account.
   */
  seatState: "active" | "no_seat" | "no_account" | "unknown" | "unconfigured";
}

export interface AgentSignature {
  id: number;
  name: string;
  email: string;
  signatureHtml: string | null;
}

export interface DashboardHighlights {
  openTickets: number;
  openByTeam: {
    teams: { teamId: number; teamName: string; count: number }[];
    unassigned: number;
  };
  sla: { breached: number; atRisk: number; measured: number };
  emails: {
    today: number;
    yesterday: number;
    monthToDate: number;
    previousMonthToDate: number;
    byRep: { userId: number; name: string; count: number; previous: number }[];
  };
}

export interface ReportRow {
  key: string;
  count: number;
  previous: number;
}

export interface TicketReport {
  periods: {
    weekStart: string;
    previousWeekStart: string;
    monthStart: string;
    previousMonthStart: string;
  };
  week: { byPipeline: ReportRow[]; byAgent: ReportRow[] };
  month: { byPipeline: ReportRow[]; byAgent: ReportRow[] };
}

export interface DashboardFilterOptions {
  teams: { id: number; name: string }[];
  channels: { id: number; displayName: string }[];
  /**
   * The teams the signed-in agent is in, as a subset of `teams`.
   *
   * What the team selector is built from for somebody who is not a manager: an
   * agent may narrow to a team they are in and to no other, and the server
   * refuses the rest rather than trusting this list. Empty for an agent in no
   * team, which the dashboard says out loud rather than showing an empty menu.
   */
  myTeams: { id: number; name: string }[];
}

export interface Automation {
  id: number;
  name: string;
  position: number;
  isActive: boolean;
  fromDomain: string | null;
  fromEmail: string | null;
  toDomain: string | null;
  toEmail: string | null;
  /** Superseded by subjectAny; still matched, and still shown if a rule has one. */
  subjectContains: string | null;
  /** Any one of these words in the subject matches. */
  subjectAny: string[] | null;
  /** Any one of these words in the first message's body matches. */
  bodyAny: string[] | null;
  /** A view the ticket enters -- `user-12`, `unassigned`, `spam`, `saved-3`. */
  viewKey: string | null;
  assignToUserId: number | null;
  setPipelineId: number | null;
  /** Take the ticket out of any pipeline. */
  clearPipeline: boolean;
}

/** Whether a canned entry fills a reply's body or its subject line. */
export type CannedKind = "body" | "subject";

export interface CannedResponse {
  id: number;
  title: string;
  /**
   * Which box this fills, and so which content field to read.
   *
   * `body` uses `bodyHtml`, `subject` uses `subjectText`, and the other is
   * empty. Anything reading the content has to branch on this -- a subject
   * template's `bodyHtml` is the empty string, so a picker that ignored the
   * kind would offer to insert nothing.
   */
  kind: CannedKind;
  /**
   * Typed as #shortcut. Unique **per kind**, not globally: the two are typed
   * into different boxes, so the same word can name a subject template and a
   * reply snippet without either becoming ambiguous where it is used.
   */
  shortcut: string | null;
  category: string | null;
  /** The reply body. Empty string on a subject template. */
  bodyHtml: string;
  /** The subject line. Null on a reply snippet. */
  subjectText: string | null;
  createdByUserId: number | null;
  isActive: boolean;
}

export interface Pipeline {
  id: number;
  name: string;
  slug: string;
  /** Time-to-next-response allowance in business minutes; null = untimed. */
  slaMinutes: number | null;
  color: string;
  position: number;
  isActive: boolean;
}

export interface Team {
  id: number;
  name: string;
  slug: string;
  members: { id: number; name: string; email: string }[];
}

export interface BankHoliday {
  id: number;
  date: string;
  name: string;
}

export interface BusinessHours {
  timeZone: string;
  /** Index 0 = Sunday. null = closed. Minutes from local midnight. */
  week: ({ start: number; end: number } | null)[];
  holidays: BankHoliday[];
}

/** Why an agent's clocks are stopped. Mirrors `AWAY_KINDS` on the API. */
export type AwayKind = "lunch" | "holiday";

/**
 * A stretch of time one agent was away, and their tickets' SLA clocks stood
 * still.
 *
 * `endedAt` is set only when somebody came back early; a period that simply
 * ran its course leaves it null and finishes at `endsAt`.
 */
export interface AwayPeriod {
  id: number;
  userId: number;
  kind: AwayKind;
  /** The London calendar day this belongs to, "YYYY-MM-DD". */
  localDate: string;
  startedAt: string;
  endsAt: string;
  endedAt: string | null;
}

/**
 * Everything the desk needs to know about who is away.
 *
 * `periods` is the whole desk's recent history, because the queue recomputes
 * the countdown on tickets belonging to other people. `mine` is today's, which
 * is what decides whether the buttons are still on offer -- a lunch already
 * taken and finished is not in `periods` as anything special, but it is the
 * reason Lunch is greyed out until tomorrow.
 */
export interface AwayState {
  periods: AwayPeriod[];
  /** Today, in the business calendar's zone. The server decides where a day starts. */
  today: string;
  mine: AwayPeriod[];
}

export interface EmailAddress {
  name?: string;
  email: string;
}

export interface Message {
  id: number;
  kind: MessageKind;
  authorUserId: number | null;
  authorName: string | null;
  authorAvatarUrl: string | null;
  fromName: string | null;
  fromEmail: string | null;
  /** The sender's number on a phone channel; null on email. */
  fromPhone: string | null;
  toRecipients: EmailAddress[] | null;
  ccRecipients: EmailAddress[] | null;
  subject: string | null;
  bodyText: string | null;
  /**
   * What this message says, with the thread quoted under it taken out.
   *
   * The API does the cutting, because the markers it cuts on -- Gmail's
   * `gmail_quote`, Outlook's reply header -- live in `class` and `id`
   * attributes that the sanitiser removes before this ever reaches a browser.
   * See the API's common/quoted-html.ts.
   */
  bodyHtml: string | null;
  /**
   * The thread that was printed under it, for the disclosure below the bubble.
   *
   * Null when the message quoted nothing, and null on an internal note, which
   * is never split. Kept rather than dropped for two reasons: a cut in the
   * wrong place has to be recoverable by clicking, and a reply built from this
   * message quotes both halves, exactly as every other mail client would.
   */
  quotedHtml: string | null;
  snippet: string | null;
  /**
   * Whether the message arrived with any attachment at all, inline ones
   * included. `attachments` lists only the non-inline files, so a mail whose
   * only attachment is a signature logo has this true and that empty.
   */
  hasAttachments: boolean;
  attachments: MessageAttachment[];
  /**
   * Images that are the message rather than files beside it, and that nothing
   * else is drawing.
   *
   * A WhatsApp sticker, which has no HTML body to sit inside. An email's
   * embedded pictures are *not* here: the API points their `cid:` references at
   * the inline route and takes them out of this list, so the bubble no longer
   * has to work out for itself which of the two it is looking at.
   */
  inlineImages: MessageAttachment[];
  /**
   * How many of this message's pictures `bodyHtml` draws itself.
   *
   * Only ever read to decide whether to keep quiet. The bubble used to tell an
   * agent that "attachments are embedded in the message above" whenever a
   * message had files and none were listed -- which is the exact shape of a
   * mail whose one attachment is a signature logo, and the logo is now visibly
   * right there.
   */
  embeddedImages: number;
  /**
   * Where the customer said they were, when the message was a location share.
   *
   * The coordinates, not the sentence: `bodyText` already carries a readable
   * "[location: ...]" for the ticket list and for search, and this is what the
   * conversation view draws a map from.
   */
  location: MessageLocation | null;
  /** The eBay listing this message's conversation is about, if any. */
  ebayItemId?: string | null;
  /**
   * The customer, when a colleague forwarded this email in.
   *
   * `fromEmail` is still the colleague -- that is who sent it to us -- and
   * this is who it is really from, read out of the forwarded header at ingest.
   * A reply goes here. Null on everything else, which is nearly everything.
   */
  forwardedFrom: { name?: string | null; email: string } | null;
  /**
   * An agent forwarded this to somebody outside the conversation.
   *
   * Recorded at send time rather than derived from the recipients or the
   * "Fwd:" subject, both of which occur on messages that are not forwards.
   * False on everything sent before the flag existed, which means "we do not
   * know", not "it was not one".
   */
  isForward: boolean;
  delivery: Delivery;
  deliveryError: string | null;
  sentAt: string;
  /**
   * When the author last rewrote this note, or null.
   *
   * Only ever set on notes -- mail is not editable. Shown in the timeline so a
   * colleague who read the original knows the words have moved.
   */
  editedAt: string | null;
}

/**
 * A downloadable file on a message.
 *
 * No storage key: the bytes are fetched through `/api/attachments/:id`, which
 * resolves the location server-side and, for inbound mail, pulls from Gmail on
 * the first request and mirrors to storage for later ones.
 */
/**
 * How a Gmail channel is authorised.
 *
 * `connected` means that inbox consented and we hold a refresh token for it
 * alone. Not connected is only a problem when there is no delegation fallback
 * configured, which is why the server reports both -- see `ChannelConnections`.
 */
export interface ChannelConnection {
  channelId: number;
  connected: boolean;
  /** The Google account that consented; equals the channel address. */
  googleEmail: string | null;
  connectedByUserId: number | null;
  connectedAt: string | null;
  /** Set when the credential stopped working and the channel needs reconnecting. */
  lastError: string | null;
}

export interface ChannelConnections {
  /** Whether this server can run the consent flow at all. */
  oauthAvailable: boolean;
  connections: ChannelConnection[];
}

/**
 * An eBay channel's seller account, as Settings needs it. Separate from
 * `ChannelConnection` because the credential is a different thing: an eBay
 * refresh token has a date on it, and that date is worth showing.
 */
export interface EbayConnection {
  channelId: number;
  connected: boolean;
  /** The seller that consented; equals the channel address. */
  ebayUsername: string | null;
  connectedByUserId: number | null;
  connectedAt: string | null;
  /** The day the channel stops unless somebody reconnects it. */
  refreshTokenExpiresAt: string | null;
  /** A shared token is also held by the NetXL listings and orders integration. */
  obtainedVia: "consent" | "shared_token" | null;
  /** Whether eBay confirmed which seller it is, rather than Pulse assuming. */
  verified: boolean;
  /** Whether the grant carries sell.inventory, which sending a photo needs. */
  canSendImages: boolean;
  lastError: string | null;
}

export interface EbayConnections {
  /** Whether this server has an eBay keyset at all. */
  available: boolean;
  /**
   * Whether it has a RuName of its own for eBay's consent flow. Without one a
   * channel is connected by pasting a refresh token instead.
   */
  consentAvailable: boolean;
  connections: EbayConnection[];
}

export interface MessageLocation {
  latitude: number;
  longitude: number;
  /** What the customer's phone called the place, when it named one. */
  name?: string | null;
  address?: string | null;
}

export interface MessageAttachment {
  id: number;
  messageId: number;
  filename: string;
  mimeType: string;
  /** Gmail's reported size. Null when it did not report one. */
  sizeBytes: number | null;
  isInline: boolean;
}

export interface TicketActivity {
  id: number;
  ticketId: number;
  /** Null for anything ingest did rather than a person. */
  actorUserId: number | null;
  actorName: string | null;
  type: string;
  data: Record<string, unknown> | null;
  /**
   * The ids in `data`, resolved to names by the server.
   *
   * Resolved there rather than here because `/users` lists only active agents,
   * and a history has to be able to name somebody who has since left.
   *
   * Null when the event carries no ids at all -- a status change moves between
   * "open" and "closed", which are already words.
   */
  fromLabel: string | null;
  toLabel: string | null;
  createdAt: string;
}

export interface TicketDetail {
  ticket: TicketSummary;
  /** `type` is what the composer branches on; see TicketsService.findDetail. */
  channel: {
    id: number;
    type: ChannelType;
    address: string;
    displayName: string;
    slug: string;
    /**
     * The email channel that answers for this one, on a channel with no address
     * of its own -- a web form. Null everywhere else, where the ticket's own
     * channel is what a reply comes from.
     */
    replyChannelId: number | null;
  };
  /**
   * The conversation already exists in Gmail, so only the channel it lives on
   * can answer it.
   *
   * The thread id itself stays on the server -- this is the one thing a client
   * needs from it. The composer hides its From menu when this is true, because
   * a Gmail thread belongs to a single mailbox and an alias reply is refused
   * both by our send path and by Gmail.
   */
  hasEmailThread: boolean;
  /**
   * Where this ticket went, on one that has been merged away.
   *
   * The row survives a merge so old links still resolve, and every list filters
   * it out -- so this is the only signal a pasted link has landed on a ticket
   * whose messages now live somewhere else. `TicketsView` redirects on it.
   */
  mergedIntoTicketId: number | null;
  messages: Message[];
  activity: TicketActivity[];
}

/**
 * Where the 24-hour WhatsApp service window stands on one ticket.
 *
 * A discriminated union rather than nullable fields, because "this channel has
 * no such concept" and "the window is closed" are different answers and a
 * composer that conflated them would show an email ticket a closed-window
 * warning.
 */
export type WhatsAppWindow =
  | { applicable: false }
  | { applicable: true; open: boolean; remainingMs: number; label: string };

export type PresenceState = "viewing" | "replying";

export interface PresenceEntry {
  userId: number;
  name: string;
  avatarUrl: string | null;
  state: PresenceState;
}

export const STATUS_LABELS: Record<TicketStatus, string> = {
  open: "Open",
  closed: "Closed",
};

/** Bullet-point summary of a ticket's conversation. */
export interface TicketThreadSummary {
  bullets: string[];
  /** False once newer messages have arrived than the summary covers. */
  fresh: boolean;
  createdAt: string;
}

// ── Lane ────────────────────────────────────────────────────────────────────

/** The Lane task a ticket is linked to, as the details panel sees it. */
export interface LaneLink {
  taskId: string;
  url: string;
  title: string | null;
  assigneeNames: string[];
  /** Set once Lane reports the task done. */
  completedAt: string | null;
  checkedAt: string | null;
}

export interface LaneStatusInfo {
  enabled: boolean;
  /** Why the key is not usable, when it is not. */
  error: string | null;
}

export interface LaneBoardOption {
  space: { id: string; name: string };
  boards: { id: string; name: string }[];
}

export interface LaneBoardStatus {
  id: string;
  name: string;
  /** Open-ended: live boards return categories beyond the documented three. */
  category: string;
}

/** Whether this agent has connected their own Lane account. */
export interface LaneConnection {
  /** False when the server has no OAuth client configured at all. */
  available: boolean;
  connected: boolean;
  laneName: string | null;
  laneEmail: string | null;
}

/** Which halves of an agent's own Google grant they gave. */
export type GoogleCapability = "calendar" | "gmail" | "send";

/**
 * Whether this agent has connected their own Google account.
 *
 * Distinct from the channel connections in Settings, which authorise shared
 * inboxes so the helpdesk can read and send as support@. This one is a person:
 * a meeting lands in their calendar and the invitation arrives from them.
 */
export interface GoogleConnection {
  /** False when the server has no OAuth client or token key configured. */
  available: boolean;
  connected: boolean;
  googleEmail: string | null;
  /**
   * What the grant actually carries, read back from Google rather than from
   * what was asked for -- the consent screen lets somebody untick things.
   */
  capabilities: GoogleCapability[];
}

export interface LaneMember {
  id: string;
  name: string | null;
  email: string;
}

/**
 * One entry on the spam allow list.
 *
 * `kind` is worked out by the server from what was typed rather than chosen on
 * the form -- somebody adding one has an address they just copied off a ticket,
 * and classifying it first is a question they should not have to answer. It
 * comes back so the row can say which of the two it turned out to be, because
 * "supplier.co.uk" allowing everybody there is worth being explicit about.
 */
export interface AllowedSender {
  id: number;
  kind: "address" | "domain";
  value: string;
  note: string | null;
  createdAt: string;
  createdByName: string | null;
}
